import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  DocumentTextIcon, 
  CalendarIcon, 
  CurrencyRupeeIcon,
  TableCellsIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface Contract {
  id: number;
  contractNumber: string;
  clientId: number;
  client: {
    id: number;
    name: string;
    director: string;
  };
  title: string;
  status: string;
}

interface Order {
  id: number;
  orderNumber: string;
  clientId: number;
  totalAmount: number;
  finalAmount: number;
  discountAmount: number;
  startDate: string;
  endDate: string;
  status: string;
  items: OrderItem[];
}

interface OrderItem {
  id: number;
  structureId: number;
  structure: {
    id: number;
    code: string;
    type: string;
    location: string;
  };
  startDate: string;
  endDate: string;
  unitPrice: number;
  quantity: number;
  totalPrice: number;
  discountPercentage: number;
  finalPrice: number;
}

interface AppendixFormData {
  id?: number;
  contractId: number;
  orderId: number;
  appendixNumber: string;
  issueDate: string;
  title: string;
  paymentMethod: string;
  paymentTerms: string;
  notes: string;
  status: string;
}

const ContractAppendixForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id && id !== 'new');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  
  const [formData, setFormData] = useState<AppendixFormData>({
    contractId: 0,
    orderId: 0,
    appendixNumber: '',
    issueDate: new Date().toISOString().split('T')[0],
    title: 'Приложение к договору',
    paymentMethod: 'банковский перевод',
    paymentTerms: 'Предоплата 100%',
    notes: '',
    status: 'draft'
  });
  
    // Generate unique appendix number
    useEffect(() => {
      if (!isEditing && selectedContract) {
        const date = new Date();
        // const year = date.getFullYear(); // Removed unused variable
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
        setFormData(prev => ({
        ...prev,
        appendixNumber: `${selectedContract.contractNumber}/П-${month}${random}`
      }));
    }
  }, [isEditing, selectedContract]);
  
  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch active contracts
        const contractsResponse = await axios.get('/contracts', { 
          params: { status: ['signed', 'active'] }
        });
        setContracts(contractsResponse.data);
        
        // If editing, fetch appendix data
        if (isEditing && id) {
          const appendixResponse = await axios.get(`/contracts/appendix/${id}`);
          const appendix = appendixResponse.data;
          
          // Format dates
          appendix.issueDate = appendix.issueDate ? new Date(appendix.issueDate).toISOString().split('T')[0] : '';
          
          setFormData(appendix);
          
          // Fetch and set selected contract
          const contractResponse = await axios.get(`/contracts/${appendix.contractId}`);
          setSelectedContract(contractResponse.data);
          
          // Fetch and set selected order
          const orderResponse = await axios.get(`/orders/${appendix.orderId}`);
          setSelectedOrder(orderResponse.data);
          
          // Fetch client orders
          const clientOrdersResponse = await axios.get('/orders', { 
            params: { 
              clientId: contractResponse.data.clientId,
              status: ['reserved', 'waitingPayment', 'partiallyPaid', 'fullyPaid'] 
            }
          });
          setOrders(clientOrdersResponse.data);
        }
        
        // If creating from a specific contract
        const contractIdFromUrl = new URLSearchParams(window.location.search).get('contractId');
        if (!isEditing && contractIdFromUrl) {
          const contractId = parseInt(contractIdFromUrl);
          const contract = contractsResponse.data.find((c: Contract) => c.id === contractId);
          
          if (contract) {
            setSelectedContract(contract);
            setFormData(prev => ({
              ...prev,
              contractId: contract.id
            }));
            
            // Fetch client orders
            const ordersResponse = await axios.get('/orders', { 
              params: { 
                clientId: contract.clientId,
                status: ['reserved', 'waitingPayment', 'partiallyPaid', 'fullyPaid'] 
              }
            });
            setOrders(ordersResponse.data);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, isEditing]);
  
  // Handle contract change
  const handleContractChange = async (contractId: number) => {
    const contract = contracts.find(c => c.id === contractId);
    setSelectedContract(contract || null);
    
    setFormData(prev => ({
      ...prev,
      contractId,
      orderId: 0
    }));
    
    if (contract) {
      try {
        const response = await axios.get('/orders', { 
          params: { 
            clientId: contract.clientId,
            status: ['reserved', 'waitingPayment', 'partiallyPaid', 'fullyPaid'] 
          }
        });
        setOrders(response.data);
      } catch (error) {
        console.error('Error fetching client orders:', error);
      }
    } else {
      setOrders([]);
    }
  };
  
  // Handle order change
  const handleOrderChange = (orderId: number) => {
    const order = orders.find(o => o.id === orderId);
    setSelectedOrder(order || null);
    
    setFormData(prev => ({
      ...prev,
      orderId
    }));
  };
  
  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (isEditing && id) {
        await axios.put(`/contracts/appendix/${id}`, formData);
      } else {
        await axios.post('/contracts/appendix', formData);
      }
      
      navigate(`/contracts/${formData.contractId}`);
    } catch (error) {
      console.error('Error saving contract appendix:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Generate preview
  const previewAppendix = async () => {
    try {
      const response = await axios.post('/contracts/appendix/preview', formData, {
        responseType: 'blob'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `appendix_preview_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error generating preview:', error);
    }
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title={isEditing ? 'Редактирование приложения' : 'Новое приложение к договору'}
        subtitle={isEditing ? `Приложение ${formData.appendixNumber}` : 'Создание нового приложения к договору'}
      />
      
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Left column */}
          <div className="space-y-6">
            {/* Contract selection */}
            <div>
              <label htmlFor="contractId" className="block text-sm font-medium text-gray-700">
                Договор <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative">
                <DocumentTextIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  id="contractId"
                  name="contractId"
                  value={formData.contractId}
                  onChange={(e) => handleContractChange(Number(e.target.value))}
                  className="pl-10 form-input block w-full"
                  required
                  disabled={isEditing}
                >
                  <option value="">Выберите договор</option>
                  {contracts.map(contract => (
                    <option key={contract.id} value={contract.id}>
                      {contract.contractNumber} - {contract.client.name}
                    </option>
                  ))}
                </select>
              </div>
              {selectedContract && (
                <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                  <p><span className="font-medium">Договор:</span> {selectedContract.title}</p>
                  <p><span className="font-medium">Клиент:</span> {selectedContract.client.name}</p>
                  <p><span className="font-medium">Статус:</span> {selectedContract.status}</p>
                </div>
              )}
            </div>
            
            {/* Order selection */}
            <div>
              <label htmlFor="orderId" className="block text-sm font-medium text-gray-700">
                Заказ <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative">
                <TableCellsIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  id="orderId"
                  name="orderId"
                  value={formData.orderId}
                  onChange={(e) => handleOrderChange(Number(e.target.value))}
                  className="pl-10 form-input block w-full"
                  required
                  disabled={!selectedContract || isEditing}
                >
                  <option value="">Выберите заказ</option>
                  {orders.map(order => (
                    <option key={order.id} value={order.id}>
                      {order.orderNumber} ({new Intl.NumberFormat('ru-KZ', { 
                        style: 'currency', 
                        currency: 'KZT',
                        maximumFractionDigits: 0 
                      }).format(order.finalAmount)})
                    </option>
                  ))}
                </select>
              </div>
              {selectedOrder && (
                <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                  <p><span className="font-medium">Заказ:</span> {selectedOrder.orderNumber}</p>
                  <p><span className="font-medium">Сумма:</span> {new Intl.NumberFormat('ru-KZ', { 
                    style: 'currency', 
                    currency: 'KZT',
                    maximumFractionDigits: 0 
                  }).format(selectedOrder.finalAmount)}</p>
                  <p><span className="font-medium">Период:</span> {new Date(selectedOrder.startDate).toLocaleDateString('ru-KZ')} - {new Date(selectedOrder.endDate).toLocaleDateString('ru-KZ')}</p>
                </div>
              )}
            </div>
            
            {/* Appendix number */}
            <div>
              <label htmlFor="appendixNumber" className="block text-sm font-medium text-gray-700">
                Номер приложения <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="appendixNumber"
                name="appendixNumber"
                value={formData.appendixNumber}
                onChange={handleChange}
                className="form-input block w-full"
                required
              />
            </div>
            
            {/* Appendix title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Название приложения <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="form-input block w-full"
                placeholder="Например: Приложение к договору на размещение рекламы"
                required
              />
            </div>
          </div>
          
          {/* Right column */}
          <div className="space-y-6">
            {/* Issue date */}
            <div>
              <label htmlFor="issueDate" className="block text-sm font-medium text-gray-700">
                Дата составления <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative">
                <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  id="issueDate"
                  name="issueDate"
                  value={formData.issueDate}
                  onChange={handleChange}
                  className="pl-10 form-input block w-full"
                  required
                />
              </div>
            </div>
            
            {/* Payment method */}
            <div>
              <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700">
                Способ оплаты <span className="text-red-500">*</span>
              </label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className="form-input block w-full"
                required
              >
                <option value="банковский перевод">Банковский перевод</option>
                <option value="наличные">Наличные</option>
                <option value="банковская карта">Банковская карта</option>
                <option value="электронный платеж">Электронный платеж</option>
              </select>
            </div>
            
            {/* Payment terms */}
            <div>
              <label htmlFor="paymentTerms" className="block text-sm font-medium text-gray-700">
                Условия оплаты <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative">
                <CurrencyRupeeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  id="paymentTerms"
                  name="paymentTerms"
                  value={formData.paymentTerms}
                  onChange={handleChange}
                  className="pl-10 form-input block w-full"
                  required
                >
                  <option value="Предоплата 100%">Предоплата 100%</option>
                  <option value="Предоплата 50%, оставшиеся 50% в конце периода">Предоплата 50%, оставшиеся 50% в конце периода</option>
                  <option value="Ежемесячная оплата равными частями">Ежемесячная оплата равными частями</option>
                  <option value="Оплата по факту выполнения работ">Оплата по факту выполнения работ</option>
                </select>
              </div>
            </div>
            
            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Статус <span className="text-red-500">*</span>
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-input block w-full"
                required
              >
                <option value="draft">Черновик</option>
                <option value="pending">Ожидает подписания</option>
                <option value="signed">Подписан</option>
                <option value="active">Активен</option>
                <option value="completed">Завершен</option>
                <option value="cancelled">Отменен</option>
              </select>
            </div>
            
            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                Примечания
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="form-input block w-full"
                placeholder="Дополнительная информация"
              />
            </div>
          </div>
        </div>
        
        {/* Order items preview */}
        {selectedOrder && selectedOrder.items && selectedOrder.items.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Конструкции в заказе</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Код
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Тип
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Локация
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Период
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Цена
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Скидка
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Итого
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedOrder.items.map(item => (
                    <tr key={item.id}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {item.structure.code}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {item.structure.type}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {item.structure.location.length > 30 
                          ? item.structure.location.substring(0, 30) + '...' 
                          : item.structure.location}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(item.startDate).toLocaleDateString('ru-KZ')} - {new Date(item.endDate).toLocaleDateString('ru-KZ')}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Intl.NumberFormat('ru-KZ', { 
                          style: 'currency', 
                          currency: 'KZT',
                          maximumFractionDigits: 0 
                        }).format(item.unitPrice)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {item.discountPercentage}%
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {new Intl.NumberFormat('ru-KZ', { 
                          style: 'currency', 
                          currency: 'KZT',
                          maximumFractionDigits: 0 
                        }).format(item.finalPrice)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                      Итого:
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {new Intl.NumberFormat('ru-KZ', { 
                        style: 'currency', 
                        currency: 'KZT',
                        maximumFractionDigits: 0 
                      }).format(selectedOrder.finalAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
        
        {/* Form actions */}
        <div className="mt-6 flex justify-between">
          <button
            type="button"
            onClick={previewAppendix}
            className="btn-secondary flex items-center"
            disabled={!formData.contractId || !formData.orderId}
          >
            <ArrowPathIcon className="h-5 w-5 mr-1" />
            Предпросмотр PDF
          </button>
          
          <div className="space-x-3">
            <button
              type="button"
              onClick={() => navigate(`/contracts/${formData.contractId}`)}
              className="btn-secondary"
            >
              Отмена
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || !formData.contractId || !formData.orderId}
            >
              {submitting ? 'Сохранение...' : isEditing ? 'Обновить приложение' : 'Создать приложение'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContractAppendixForm;
